import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import apiClient from '../api/config';
import { useTenantCode } from './useTenantCode';


export const useMyRecoveries = () => {
  const tenantCode = useTenantCode();
  return useQuery({
    queryKey: ['client-portal', 'my-recoveries', tenantCode],
    queryFn: () => apiClient.get(`/${tenantCode}/portal/my-recoveries`).then((r) => r.data),
    enabled: !!tenantCode,
  });
};

export interface ClientLevel {
  _id: string;
  nombre: string;
  color?: string;
}

export interface ScheduleEvent {
  _id: string;
  nivel_id: { _id: string; nombre: string; color?: string } | string;
  instructor_id: { nombre: string } | string;
  sala: string;
  fecha: string;
  horaInicio: string;
  horaFin: string;
  maxClientes: number;
  availableSlots: number;
  isFull: boolean;
  isEnrolled: boolean;
  /**
   * Hasta cuándo valdría la recuperación si se cancelase esta clase (ISO). Lo
   * calcula el backend porque depende del calendario (fin de mes, último lunes);
   * solo depende de la fecha de la clase, así que no caduca con el reloj.
   */
  recoveryDeadline?: string;
  /** D1 — the client is queued for this class (only their OWN queue state). */
  onWaitlist?: boolean;
  /** D1 — id of that queue entry, needed to leave it. */
  waitlistEntryId?: string | null;
  /** D1 — class start as an ISO instant, resolved in Madrid by the backend. */
  startsAt?: string;
}

/** D1 — how a tenant runs its waitlist. */
export type WaitlistMode = 'off' | 'auto' | 'manual';

/**
 * P5 — one of the centre's holidays, as the portal sends it. `fecha` and
 * `descripcion` keep the Spanish names of the `festivos` collection they come
 * from; renaming them here would only hide the mapping.
 */
export interface ScheduleHoliday {
  fecha: string;
  descripcion?: string;
}

export interface ScheduleResponse {
  events: ScheduleEvent[];
  /**
   * P5 — holidays inside the visible window, from today onward. They arrive here
   * rather than through `GET /festivos`, which requires role `user` and answers a
   * client with a 403. Absent (older backend) = none painted, as before.
   */
  holidays?: ScheduleHoliday[];
  config: {
    minCancellationHours: number;
    rescheduleDays: number;
    lastWeekCarryover?: boolean;
    cancellationHorizonDays?: number | null;
    /** Meses de calendario que el centro muestra, contando el actual. */
    visibleMonths?: number;
    /** Último instante visible (ISO): fin del último día del último mes mostrado. */
    visibleUntil?: string;
    /**
     * R15 — primer instante visible (ISO): el día 1 del mes pasado. El back dejó
     * de traerse todas las clases desde que el centro abrió, así que la
     * navegación tiene que pararse aquí o el alumno llega a un mes en blanco que
     * en realidad sí tuvo clases. Ausente (back antiguo) = sin tope por abajo,
     * como antes.
     */
    visibleFrom?: string;
    /** D1 — 'off' hides the waitlist entirely. */
    waitlistMode?: WaitlistMode;
    /** D1 — no joining (and no promotion) this close to the class start. */
    waitlistCutoffMinutes?: number;
    /**
     * P1 — a recovery can be spent in ANY level, not just the ones the client
     * holds a slot in. Absent (older backend) reads as the restricted rule.
     */
    recoveryAnyLevel?: boolean;
  };
}

// EVERY scheduled class of the center (any level, full or not), annotated for the
// calendar. The level rule decides what is BOOKABLE, never what is visible.
export const useSchedule = () => {
  const tenantCode = useTenantCode();
  return useQuery<ScheduleResponse>({
    queryKey: ['client-portal', 'schedule', tenantCode],
    queryFn: () => apiClient.get(`/${tenantCode}/portal/schedule`).then((r) => r.data),
    enabled: !!tenantCode,
  });
};

// Levels in the client's profile, derived from their active plazas.
export const useMyLevels = () => {
  const tenantCode = useTenantCode();
  return useQuery<ClientLevel[]>({
    queryKey: ['client-portal', 'my-levels', tenantCode],
    queryFn: () => apiClient.get(`/${tenantCode}/portal/my-levels`).then((r) => r.data),
    enabled: !!tenantCode,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCancelClass = () => {
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      apiClient.post(`/${tenantCode}/portal/cancel-class`, { eventId }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-portal'] });
    },
  });
};

export const useRescheduleClass = () => {
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { recoveryId: string; newEventId: string }) =>
      apiClient.post(`/${tenantCode}/portal/reschedule-class`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-portal'] });
      toast.success('Recuperación reservada');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'No se pudo reservar la recuperación';
      toast.error(message);
    },
  });
};

const serverMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

// D1 — join a full class's waitlist.
export const useJoinWaitlist = () => {
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      apiClient.post(`/${tenantCode}/portal/waitlist`, { eventId }).then((r) => r.data),
    onSuccess: () => {
      // The schedule carries `onWaitlist`, so it has to be refetched.
      queryClient.invalidateQueries({ queryKey: ['client-portal'] });
      toast.success('Te has apuntado a la lista de espera. Te hemos enviado un correo con los detalles.');
    },
    // The server explains exactly why joining isn't possible (free seats, no
    // valid recovery, too close to the start…), so show it verbatim.
    onError: (error: unknown) => {
      toast.error(serverMessage(error, 'No se pudo apuntar a la lista de espera'));
    },
  });
};

// D1 — leave a waitlist.
export const useLeaveWaitlist = () => {
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) =>
      apiClient.delete(`/${tenantCode}/portal/waitlist/${entryId}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-portal'] });
      toast.success('Has salido de la lista de espera');
    },
    onError: (error: unknown) => {
      toast.error(serverMessage(error, 'No se pudo salir de la lista de espera'));
    },
  });
};
